/**
 * Product form with button-based option pickers.
 *
 * Markup contract:
 *   <product-form>
 *     <form action="/cart/add" method="post">
 *       <input type="hidden" name="id" data-variant-id value="<initial-id>">
 *       <fieldset data-option data-option-position="1" data-option-name="Color">
 *         <button type="button" data-option-value="Peach" class="pp-color on">…</button>
 *         <button type="button" data-option-value="Black"  class="pp-color">…</button>
 *       </fieldset>
 *       <fieldset data-option data-option-position="2" data-option-name="Size">
 *         <button type="button" data-option-value="M" class="pp-size on">M</button>
 *         …
 *       </fieldset>
 *       <button type="submit">Add</button>
 *     </form>
 *     <script type="application/json" data-variants>[…]</script>
 *   </product-form>
 *
 * On click of a non-disabled option button, the active class (`on`) flips
 * within its fieldset, the `[data-option-display]` element (if present)
 * updates to show the current value, the matching variant is resolved
 * from data-variants, the hidden `id` input is updated, the submit button
 * label/availability is re-rendered, and a `variant:change` CustomEvent
 * is dispatched so the gallery (and others) can react.
 */
class ProductForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector("form");
    this.submitButton = this.form?.querySelector('[type="submit"]');
    this.idInput = this.form?.querySelector("[data-variant-id]");
    this.priceEl = document.querySelector("[data-product-price]");
    this.compareEl = document.querySelector("[data-product-compare]");
    this.saveEl = document.querySelector("[data-product-save]");
    this.urgencyEl = document.querySelector("[data-product-urgency]");

    const dataEl = this.querySelector("[data-variants]");
    try {
      this.variants = dataEl ? JSON.parse(dataEl.textContent) : [];
    } catch {
      this.variants = [];
    }

    this.fieldsets = [...this.querySelectorAll("[data-option]")];

    this.fieldsets.forEach((fs) => {
      fs.addEventListener("click", (e) => {
        const btn = e.target.closest("[data-option-value]");
        if (!btn || btn.disabled || btn.classList.contains("sold")) return;
        e.preventDefault();
        this.#selectOption(fs, btn);
      });
    });

    this.form?.addEventListener("submit", (e) => this.#handleSubmit(e));

    this.#refreshSoldOut();
  }

  #selectedValuesByPosition() {
    const values = [];
    this.fieldsets.forEach((fs) => {
      const pos = parseInt(fs.dataset.optionPosition, 10) - 1;
      const active = fs.querySelector("[data-option-value].on");
      values[pos] = active?.dataset.optionValue ?? null;
    });
    return values;
  }

  #findVariant(values) {
    return this.variants.find((v) =>
      v.options.every((opt, i) => opt === values[i]),
    );
  }

  #selectOption(fieldset, button) {
    fieldset
      .querySelectorAll("[data-option-value]")
      .forEach((b) => b.classList.remove("on"));
    button.classList.add("on");

    const display = fieldset.querySelector("[data-option-display]");
    if (display) display.textContent = button.dataset.optionValue;

    const values = this.#selectedValuesByPosition();
    const variant = this.#findVariant(values);

    this.#applyVariant(variant);
    this.#refreshSoldOut();

    if (variant) {
      const url = new URL(window.location.href);
      url.searchParams.set("variant", variant.id);
      window.history.replaceState({}, "", url);
    }

    document.dispatchEvent(
      new CustomEvent("variant:change", { detail: { variant } }),
    );
  }

  #applyVariant(variant) {
    if (!this.idInput) return;
    if (!variant) {
      this.idInput.value = "";
      this.#setSubmitState({ available: false, label: "Unavailable" });
      return;
    }
    this.idInput.value = String(variant.id);
    const baseLabel = this.submitButton?.dataset.labelBase || "Add to Bag";
    this.#setSubmitState({
      available: variant.available,
      label: variant.available
        ? `${baseLabel} · ${this.#formatMoney(variant.price)}`
        : "Sold Out",
    });

    if (this.priceEl) this.priceEl.textContent = this.#formatMoney(variant.price);
    if (this.compareEl) {
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        this.compareEl.textContent = this.#formatMoney(variant.compare_at_price);
        this.compareEl.hidden = false;
        if (this.saveEl) {
          this.saveEl.textContent = `SAVE ${this.#formatMoney(variant.compare_at_price - variant.price)}`;
          this.saveEl.hidden = false;
        }
      } else {
        this.compareEl.hidden = true;
        if (this.saveEl) this.saveEl.hidden = true;
      }
    }

    if (this.urgencyEl) {
      const qty = variant.inventory_quantity;
      const tracked = variant.inventory_management;
      const showUrgency =
        variant.available &&
        tracked &&
        typeof qty === "number" &&
        qty > 0 &&
        qty <= 9;
      this.urgencyEl.hidden = !showUrgency;
      if (showUrgency) {
        const numEl = this.urgencyEl.querySelector("[data-urgency-count]");
        if (numEl) numEl.textContent = qty;
      }
    }
  }

  #setSubmitState({ available, label }) {
    if (!this.submitButton) return;
    this.submitButton.disabled = !available;
    this.submitButton.textContent = label;
  }

  /**
   * For each fieldset, mark a value `.sold` if no variant exists where
   * the value is set and all OTHER fieldsets keep their current selection.
   */
  #refreshSoldOut() {
    const current = this.#selectedValuesByPosition();
    this.fieldsets.forEach((fs) => {
      const pos = parseInt(fs.dataset.optionPosition, 10) - 1;
      fs.querySelectorAll("[data-option-value]").forEach((btn) => {
        const test = [...current];
        test[pos] = btn.dataset.optionValue;
        const v = this.#findVariant(test);
        const sold = !v || !v.available;
        btn.classList.toggle("sold", sold && !btn.classList.contains("on"));
        btn.disabled = sold && !btn.classList.contains("on");
      });
    });
  }

  #formatMoney(cents) {
    if (window.Shopify?.formatMoney) {
      const fmt = window.theme_money_format || "${{amount}}";
      return window.Shopify.formatMoney(cents, fmt);
    }
    // Minimal fallback that respects the shop's money_format string
    // (set on window.theme_money_format in theme.liquid). Handles the
    // four canonical Shopify placeholders. Without this, every non-USD
    // shop renders variant prices as "$X.XX".
    const fmt = window.theme_money_format || "${{amount}}";
    const amount = (cents / 100).toFixed(2);
    const amountInt = String(Math.round(cents / 100));
    const withSeparator = (s, sep) => s.replace(/\B(?=(\d{3})+(?!\d))/g, sep);
    return fmt
      .replace(/\{\{\s*amount\s*\}\}/g, withSeparator(amount, ","))
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, withSeparator(amountInt, ","))
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, withSeparator(amount.replace(".", ","), "."))
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, withSeparator(amountInt, "."))
      .replace(/\{\{\s*amount_with_space_separator\s*\}\}/g, withSeparator(amount.replace(".", ","), " "))
      .replace(/\{\{\s*amount_no_decimals_with_space_separator\s*\}\}/g, withSeparator(amountInt, " "));
  }

  async #handleSubmit(e) {
    e.preventDefault();
    if (!this.submitButton || this.submitButton.disabled) return;

    this.submitButton.disabled = true;
    this.submitButton.setAttribute("aria-busy", "true");
    const originalText = this.submitButton.textContent;
    this.submitButton.textContent = "Adding…";

    try {
      const formData = new FormData(this.form);
      const response = await fetch("/cart/add.js", {
        method: "POST",
        body: formData,
      });
      if (!response.ok) throw new Error(`Add to cart failed: ${response.status}`);

      const addedItem = await response.json();
      const cartResponse = await fetch("/cart.js");
      const cart = await cartResponse.json();

      document.dispatchEvent(
        new CustomEvent("cart:updated", { detail: { item_count: cart.item_count } }),
      );
      document.dispatchEvent(new CustomEvent("cart:open"));

      this.#announce(
        `Added ${addedItem.product_title} to cart. Cart now has ${cart.item_count} ${cart.item_count === 1 ? "item" : "items"}.`,
      );
    } catch (error) {
      console.error("Add to cart error:", error);
      this.submitButton.textContent = "Error — try again";
      this.#announce("Failed to add item to cart. Please try again.");
      setTimeout(() => {
        this.submitButton.textContent = originalText;
      }, 2000);
      this.submitButton.removeAttribute("aria-busy");
      this.submitButton.disabled = false;
      return;
    }

    this.submitButton.disabled = false;
    this.submitButton.removeAttribute("aria-busy");
    this.submitButton.textContent = originalText;
  }

  #announce(message) {
    const status = document.getElementById("cart-status");
    if (status) status.textContent = message;
  }
}

customElements.define("product-form", ProductForm);

export { ProductForm };
